import * as $ from 'jquery';
import { Duration } from 'luxon';

import { CustomComponent, DateUtil } from '../common'

import { TimeRangeComponent } from './time-range.component';
import { TimeRangeDurationState } from './time-range-duration-state';

import TEMPLATE from './time-range-display.component.pug';
import INFO_POPOVER_TEMPLATE from './time-range-display.info-popover.component.pug';
import INFO_ITEM_TEMPLATE from './time-range-display.info-item.component.pug';
import './time-range-display.component.scss';

const COMPONENT_SELECTOR = 'iw-time-range-display';

const ATTR_SRC = 'src';
const ATTR_MSG_INFO_CROSSES_MIDNIGHT = 'msg-info-crosses-midnight';
const ATTR_EMPTY_STATE = 'empty-state';

export interface TimeRangeDisplayElement extends HTMLElement {
  // placeholder for now
}

declare global {
  interface HTMLElementTagNameMap {
    'iw-time-range-display': TimeRangeDisplayElement;
  }
  interface JQuery {
    iwTimeRangeDisplay(): JQuery<TimeRangeDisplayElement>;
  }
}

export class TimeRangeDisplayComponent extends CustomComponent<TimeRangeDisplayElement> {

  public static readonly COMPONENT_SELECTOR = COMPONENT_SELECTOR;
  public static readonly TEMPLATE = TEMPLATE;

  public static loadPlugin(): void {
    super.loadPlugin();
    if (!$.fn.iwTimeRangeDisplay) {
      $.fn.iwTimeRangeDisplay = this.jQueryPlugin('TimeRangeDisplayComponent');
    }
  }

  public get srcSelector(): string {
    return this.$el.attr(ATTR_SRC);
  }

  public get msgInfoCrossesMidnight(): string {
    return this.$el.attr(ATTR_MSG_INFO_CROSSES_MIDNIGHT) || 'Crosses into the next day';
  }

  public get emptyState(): string {
    return this.$el.attr(ATTR_EMPTY_STATE) || '';
  }

  public readonly $src: JQuery;
  public readonly srcCtrl: TimeRangeComponent;
  public readonly $current: JQuery;
  public readonly $diff: JQuery;
  public readonly $info: JQuery;
  public readonly $infoPopoverButton: JQuery<HTMLButtonElement>;

  constructor($el: JQuery) {
    super($el);

    this.$src = $(this.srcSelector);
    this.srcCtrl = CustomComponent.getController(this.$src);
    if (!this.srcCtrl || !(this.srcCtrl instanceof TimeRangeComponent)) {
      throw new Error(`TimeRangeDisplayComponent: expected the "src" element to have a ${TimeRangeComponent.COMPONENT_SELECTOR} instance`);
    }

    this.$current = this.$el.find('.iw-time-range__current');
    this.$diff = this.$el.find('.iw-time-range__diff');
    this.$info = this.$el.find('.iw-time-range__info');
    this.$infoPopoverButton = this.$info.find('button');

    this.$src.on('change', this.onSrcChange.bind(this));
    this.onSrcChange();
  }

  protected onSrcChange(): void {
    const state = this.srcCtrl.value;

    this.updateCurrentHours(state?.current);
    this.updateDiffHours(state?.diff);
    this.updateInfo(state);
  }

  protected updateCurrentHours(duration: Duration): void {
    this.$current.text(duration ? DateUtil.formatHours(duration.as('hours')) : this.emptyState);
  }

  protected updateDiffHours(duration: Duration): void {
    const hasDiff = duration !== undefined && duration.valueOf() !== 0;
    this.$diff
      .text(DateUtil.formatHoursDiff(duration))
      .toggle(hasDiff);
  }

  protected updateInfo(state: TimeRangeDurationState): void {
    // required to reset the HTML content in the popover
    this.$infoPopoverButton.popover('dispose');

    if (!state?.info) {
      this.$info.hide();
      return;
    }

    const infoItems = [];
    if (state.info.crossesMidnight) {
      infoItems.push(this.infoMessageContent('crosses-midnight', this.msgInfoCrossesMidnight));
    }
    if (state.info.offsetChange) {
      infoItems.push(this.infoMessageContent('offset-change', DateUtil.formatDstChange(state.info.offsetChange)));
    }

    this.$info.show();

    const $popoverContent = $(INFO_POPOVER_TEMPLATE).append(infoItems);
    const popoverContentHtml = $('<div></div>').append($popoverContent).html();

    this.$infoPopoverButton.popover({ content: popoverContentHtml });
  }

  protected infoMessageContent(type: string, msg: string): JQuery {
    return $(INFO_ITEM_TEMPLATE).addClass(`iw-time-range__info__popover__item--${type}`).text(msg)
  }
}
